
import { defineComponent, ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'

interface SavedCalculation {
  id: string
  strategyId: number
  strategyName: string
  keyVolume: number
  keyProductName: string
  productCount: number
  timestamp: number
  productLines: any[]
  products: any[]
}

export default defineComponent({
  name: 'LegVolumesHistoryPanel',
  setup() {
    const router = useRouter()
    const isOpen = ref(false)
    const savedCalculations = ref<SavedCalculation[]>([])

    const STORAGE_KEY = 'leg_volumes_history'

    const loadHistory = () => {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          savedCalculations.value = JSON.parse(stored)
        } catch (e) {
          console.error('Error loading history:', e)
          savedCalculations.value = []
        }
      }
    }

    const saveHistory = () => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedCalculations.value))
    }

    const addCalculation = (calculation: Omit<SavedCalculation, 'id' | 'timestamp'>) => {
      // Check for duplicate - same strategy, key product, and key volume
      const isDuplicate = savedCalculations.value.some(item => 
        item.strategyId === calculation.strategyId &&
        item.keyProductName === calculation.keyProductName &&
        item.keyVolume === calculation.keyVolume
      )
      
      if (isDuplicate) {
        // If duplicate exists, move it to the top by removing and re-adding
        savedCalculations.value = savedCalculations.value.filter(item => 
          !(item.strategyId === calculation.strategyId &&
            item.keyProductName === calculation.keyProductName &&
            item.keyVolume === calculation.keyVolume)
        )
      }
      
      const newItem: SavedCalculation = {
        ...calculation,
        id: `calc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now()
      }
      
      // Add to beginning of array (most recent first)
      savedCalculations.value.unshift(newItem)
      
      // Keep only last 20 calculations
      if (savedCalculations.value.length > 20) {
        savedCalculations.value = savedCalculations.value.slice(0, 20)
      }
      
      saveHistory()
    }

    const removeItem = (index: number) => {
      savedCalculations.value.splice(index, 1)
      saveHistory()
    }

    const clearHistory = () => {
      if (confirm('Are you sure you want to clear all history?')) {
        savedCalculations.value = []
        saveHistory()
      }
    }

    const openCalculation = (item: SavedCalculation) => {
      // Navigate to the strategy edit page
      router.push({
        name: 'StrategiesUpdate',
        params: { id: item.strategyId },
        query: { 
          openCalculator: 'true',
          keyVolume: item.keyVolume.toString()
        }
      })
    }

    const togglePanel = () => {
      isOpen.value = !isOpen.value
      localStorage.setItem('leg_volumes_panel_open', isOpen.value.toString())
    }

    const formatDate = (timestamp: number) => {
      const date = new Date(timestamp)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      
      if (date.toDateString() === today.toDateString()) {
        return `Today ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
      } else if (date.toDateString() === yesterday.toDateString()) {
        return `Yesterday ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      }
    }

    onMounted(() => {
      loadHistory()
      
      // Restore panel state
      const savedState = localStorage.getItem('leg_volumes_panel_open')
      if (savedState === 'true') {
        isOpen.value = true
      }

      // Listen for custom event to add new calculation
      window.addEventListener('leg-volume-calculated', (event: any) => {
        addCalculation(event.detail)
      })
    })

    return {
      isOpen,
      savedCalculations,
      togglePanel,
      removeItem,
      clearHistory,
      openCalculation,
      formatDate
    }
  }
})
